import { z } from "zod";
import {
  HarmonyShoppingCart,
  HarmonyWardrobe,
} from "@/components/icons/harmony-icons-inline";
import {
  routeHandle,
  type CommerceParams,
  type ModuleBlueprint,
} from "../registry/types";

/**
 * `commerce` blueprint — alışveriş motoru. Fashion ve Technology bunu farklı
 * `vertical` ile çalıştırır. Sayfalar mevcut feature'lardan lazy import edilir;
 * hangi dikeyde olduklarını `useModuleParams<CommerceParams>()` ile öğrenirler.
 *
 * Segment sözlüğü (basePath'e göre):
 *   ""            → modül landing'i (hero + dock composer + girişler)
 *   shop          → vitrin (eski /shopping)
 *   favorites     → favori ürünler (eski /shopping/favorites)
 *   search        → anlık arama (eski /instant-search)
 *   product/:id   → ürün detayı (eski /product/:id)
 *   wardrobe      → gardırop (eski /wardrobe)      [features.wardrobe]
 *   my-wardrobe   → gardırobum (eski /my-wardrobe) [features.wardrobe]
 */

export const COMMERCE_SEGMENTS = {
  shop: "shop",
  favorites: "favorites",
  search: "search",
  product: "product",
  wardrobe: "wardrobe",
  myWardrobe: "my-wardrobe",
} as const;

const commerceParamsSchema: z.ZodType<CommerceParams> = z.object({
  vertical: z.string().min(1),
  features: z
    .object({
      wardrobe: z.boolean().optional(),
    })
    .optional(),
});

export const commerceBlueprint: ModuleBlueprint<CommerceParams> = {
  id: "commerce",
  paramsSchema: commerceParamsSchema,
  chat: { enabled: true, agentId: "commerce" },

  routes: ({ params }) => {
    const wardrobeEnabled = params.features?.wardrobe === true;

    return [
      {
        index: true,
        lazy: async () => ({
          Component: (await import("@/features/commerce-landing"))
            .CommerceLandingPage,
        }),
        // Misafir landing'i görür; composer AuthGate açar, veri şeritleri gizli
        handle: routeHandle({ auth: "optional" }),
      },
      {
        path: COMMERCE_SEGMENTS.shop,
        lazy: async () => ({
          Component: (await import("@/features/shopping/ShoppingPage"))
            .ShoppingPage,
        }),
        handle: routeHandle({ auth: "private" }),
      },
      {
        path: COMMERCE_SEGMENTS.favorites,
        lazy: async () => ({
          Component: (await import("@/features/favorites")).FavoritesPage,
        }),
        // Favoriler kullanıcının kendi listesi — misafire gösterilecek bir
        // hali yok, uç da AuthGuard altında.
        handle: routeHandle({ auth: "private" }),
      },
      {
        path: COMMERCE_SEGMENTS.search,
        lazy: async () => ({
          Component: (await import("@/pages/InstantSearchPage")).default,
        }),
        handle: routeHandle({ auth: "private" }),
      },
      {
        path: `${COMMERCE_SEGMENTS.product}/:id`,
        lazy: async () => ({
          Component: (
            await import("@/features/product-detail/ProductDetailPage")
          ).ProductDetailPage,
        }),
        // Ürün detayı bugün guard'sız (paylaşılabilir link)
        handle: routeHandle({ auth: "public" }),
      },
      ...(wardrobeEnabled
        ? [
            {
              path: COMMERCE_SEGMENTS.wardrobe,
              lazy: async () => ({
                Component: (await import("@/pages/WardrobePage")).WardrobePage,
              }),
              handle: routeHandle({ auth: "private" }),
            },
            {
              path: COMMERCE_SEGMENTS.myWardrobe,
              lazy: async () => ({
                Component: (await import("@/pages/MyWardrobePage")).default,
              }),
              handle: routeHandle({ auth: "private" }),
            },
          ]
        : []),
    ];
  },

  nav: ({ params }) => [
    {
      id: "shop",
      labelKey: "common.shopping",
      icon: HarmonyShoppingCart,
      to: COMMERCE_SEGMENTS.shop,
    },
    ...(params.features?.wardrobe === true
      ? [
          {
            id: "wardrobe",
            labelKey: "wardrobe.title",
            icon: HarmonyWardrobe,
            to: COMMERCE_SEGMENTS.wardrobe,
          },
        ]
      : []),
  ],
};
